import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { httpClient, getApiUrl } from '../utils/httpClient';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  LinearProgress, 
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Analytics as AnalyticsIcon,
  AccountBalance as AccountBalanceIcon,
  Science as ScienceIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface BillUploadProps {
  onAnalysisStart: () => void;
  onAnalysisComplete: (result: any) => void;
}

const BillUpload: React.FC<BillUploadProps> = ({ onAnalysisStart, onAnalysisComplete }) => {
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [processingStatus, setProcessingStatus] = useState<string>('');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadError('');
    setUploadProgress(0);
    setProcessingStatus('');
    onAnalysisStart();

    try {
      // Step 1: Upload the bill with progress tracking
      setProcessingStatus('📤 Uploading your bill...');
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await httpClient.postFormData(
        getApiUrl('/upload-bill'),
        formData
      );
      
      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const uploadData = await uploadResponse.json();

      // Step 2: Enhanced processing phase with detailed progress updates
      const processingMessages = [
        { msg: file.name.endsWith('.pdf') ? '📄 Extracting bill data from PDF document...' : '📊 Parsing CSV bill structure and line items...', progress: 40 },
        { msg: '🔍 Identifying AWS services and usage patterns...', progress: 45 },
        { msg: '💰 Categorizing costs by service and region...', progress: 50 },
        { msg: '📈 Analyzing usage trends and peaks...', progress: 55 },
        { msg: '🎯 Detecting optimization opportunities...', progress: 60 },
        { msg: '🔄 Finding OCI service equivalents...', progress: 65 },
        { msg: '⚖️ Comparing instance types and configurations...', progress: 70 }
      ];

      let messageIndex = 0;
      setProcessingStatus(processingMessages[0].msg);
      setUploadProgress(processingMessages[0].progress);

      const progressInterval = setInterval(() => {
        messageIndex++;
        if (messageIndex < processingMessages.length) {
          setProcessingStatus(processingMessages[messageIndex].msg);
          setUploadProgress(processingMessages[messageIndex].progress);
        } else {
          clearInterval(progressInterval);
        }
      }, 1200); // Change message every 1.2 seconds

      // Step 3: Analysis phase with Claude AI
      setTimeout(async () => {
        setProcessingStatus('🧠 Initiating Claude AI analysis...');
        setUploadProgress(75);
        
        setTimeout(() => {
          setProcessingStatus('🤖 Generating intelligent migration insights...');
          setUploadProgress(80);
        }, 1000);

        setTimeout(() => {
          setProcessingStatus('📋 Creating executive summary and recommendations...');
          setUploadProgress(85);
        }, 2000);

        try {
          const analysisResponse = await httpClient.post(
            getApiUrl('/analyze-bill'),
            uploadData.data
          );

          if (!analysisResponse.ok) {
            const errorData = await analysisResponse.json();
            throw new Error(errorData.detail || 'Analysis failed');
          }

          const analysisData = await analysisResponse.json();

          clearInterval(progressInterval);
          setProcessingStatus('✅ Analysis complete!');
          setUploadProgress(100);

          // Add filename to the response
          const fileName = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
          const cleanFileName = fileName.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()); // Clean and capitalize
          analysisData.bill_name = cleanFileName;
          
          setTimeout(() => {
            onAnalysisComplete(analysisData);
            setProcessingStatus('');
            setUploadProgress(0);
          }, 800);
        } catch (analysisError: any) {
          clearInterval(progressInterval);
          setUploadError(analysisError.message || 'Failed to analyze bill');
          setProcessingStatus('');
          setUploadProgress(0);
        }
      }, 2000);

    } catch (error: any) {
      setUploadError(error.message || 'Failed to upload bill');
      setProcessingStatus('');
      setUploadProgress(0);
    }
  }, [onAnalysisStart, onAnalysisComplete]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/json': ['.json'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const loadSampleBill = async (billType: string) => {
    setUploadError('');
    setProcessingStatus('');
    setUploadProgress(0);
    onAnalysisStart();

    try {
      // Enhanced sample loading with realistic progress
      const sampleName = sampleDataDetails[billType]?.name || 'Sample Company';
      const sampleCost = sampleDataDetails[billType]?.cost || '$0/month';
      
      setProcessingStatus(`📋 Loading ${sampleName} sample (${sampleCost})...`);
      setUploadProgress(20);

      setTimeout(() => {
        setProcessingStatus('🔍 Parsing sample AWS services and costs...');
        setUploadProgress(35);
      }, 400);

      setTimeout(() => {
        setProcessingStatus('⚡ Applying real-world analysis patterns...');
        setUploadProgress(50);
      }, 800);

      setTimeout(() => {
        setProcessingStatus('🎯 Identifying optimization opportunities...');
        setUploadProgress(65);
      }, 1200);

      setTimeout(() => {
        setProcessingStatus('🔄 Mapping to optimal OCI equivalents...');
        setUploadProgress(75);
      }, 1600);

      const response = await httpClient.post(
        getApiUrl(`/sample-bill/${billType}`)
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load sample bill');
      }

      const responseData = await response.json();

      setProcessingStatus('✅ Sample analysis complete!');
      setUploadProgress(100);

      // Add bill name to the response
      const billNames = {
        'startup': 'Startup',
        'enterprise': 'Enterprise', 
        'ml_workload': 'ML/AI Workload',
        'super_enterprise': 'Super Enterprise'
      };
      responseData.bill_name = billNames[billType as keyof typeof billNames] || billType;
      
      setTimeout(() => {
        onAnalysisComplete(responseData);
        setProcessingStatus('');
        setUploadProgress(0);
      }, 400);
    } catch (error: any) {
      setUploadError(error.message || 'Failed to load sample bill');
      setProcessingStatus('');
      setUploadProgress(0);
    }
  };

  const [showSamplePreview, setShowSamplePreview] = useState(false);
  const [selectedSample, setSelectedSample] = useState('');

  const [sampleDataDetails, setSampleDataDetails] = useState<any>({});

  // Load sample bills from backend API (real data from CSV files)
  useEffect(() => {
    const loadSampleBills = async () => {
      try {
        // Get sample bill metadata from backend
        const metadataResponse = await httpClient.get(getApiUrl('/sample-bills'));
        if (!metadataResponse.ok) {
          console.error('Failed to load sample bills metadata');
          return;
        }
        
        const { sample_bills } = await metadataResponse.json();
        const updatedDetails: any = {};
        
        // Load CSV data for each sample bill
        for (const bill of sample_bills) {
          const csvFileName = `${bill.id === 'ml_workload' ? 'ml-workload' : bill.id === 'super_enterprise' ? 'super-enterprise' : bill.id === 'enterprise' ? 'enterprise-corporation' : 'startup-company'}.csv`;
          
          try {
            const csvResponse = await httpClient.get(getApiUrl(`/sample-bills/${csvFileName}`));
            if (csvResponse.ok) {
              const csvData = await csvResponse.json();
              updatedDetails[bill.id] = {
                name: bill.name,
                cost: `$${bill.monthly_cost.toLocaleString()}/month`,
                description: bill.description,
                services: bill.services,
                csvData: csvData.csv_data,
                csvFile: csvFileName,
                use_case: bill.use_case
              };
            }
          } catch (error) {
            console.error(`Failed to load CSV for ${bill.id}:`, error);
          }
        }
        
        setSampleDataDetails(updatedDetails);
      } catch (error) {
        console.error('Failed to load sample bills:', error);
      }
    };

    loadSampleBills();
  }, []); // Run once on mount

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 'var(--spacing-lg)', fontFamily: 'var(--font-family)' }}>
      {/* Main Content - 2-Column Layout for MacBook 14 */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, 
        gap: 'var(--spacing-xxl)', 
        mb: 'var(--spacing-xxl)'
      }}>
        
        {/* Upload Section */}
        <Card sx={{ 
          height: 'fit-content',
          background: 'var(--white)',
          borderRadius: 'var(--border-radius-large)',
          boxShadow: 'var(--shadow-subtle)',
          border: '1px solid var(--border-gray)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 'var(--shadow-medium)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              component="h2" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: 'var(--dark-text)', 
                fontSize: 'var(--app-title-size)',
                fontWeight: 700,
                fontFamily: 'var(--font-family)'
              }}
            >
              <CloudUploadIcon sx={{ mr: 1, color: 'primary.main' }} />
              Upload AWS Bill
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 'var(--spacing-lg)', 
                color: 'var(--medium-gray)',
                fontFamily: 'var(--font-family)',
                fontSize: 'var(--body-text-size)'
              }}
            >
              <strong>PDF (recommended)</strong>, CSV, JSON, XLSX formats supported
            </Typography>

            <Paper
              {...getRootProps()}
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                backgroundColor: isDragActive ? 'primary.50' : 'grey.50',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                borderRadius: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50'
                }
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1, opacity: 0.8 }} />
              {isDragActive ? (
                <Typography variant="body1" color="primary.main" sx={{ fontWeight: 500 }}>
                  Drop file here
                </Typography>
              ) : (
                <Box>
                  <Typography variant="body1" gutterBottom sx={{ color: 'grey.700', fontWeight: 500 }}>
                    Drag & drop or click to browse
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="small" 
                    sx={{ 
                      px: 'var(--spacing-xxl)',
                      background: 'var(--primary-gradient)',
                      borderRadius: 'var(--border-radius-small)',
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                      fontFamily: 'var(--font-family)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 'var(--shadow-medium)',
                        background: 'var(--primary-gradient)'
                      }
                    }}
                  >
                    Choose File
                  </Button>
                </Box>
              )}
            </Paper>

            {uploadProgress > 0 && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: uploadProgress === 100 ? 'linear-gradient(90deg, var(--success-green), #66bb6a)' : 'var(--primary-gradient)'
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ color: 'grey.700', fontWeight: 500 }}>
                    {processingStatus || `Processing... ${uploadProgress}%`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {uploadProgress}%
                  </Typography>
                </Box>
              </Box>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Sample Data with Preview */}
        <Card sx={{ 
          background: 'var(--purple-bg)', 
          border: '2px solid var(--purple-enterprise)', 
          borderRadius: 'var(--border-radius-large)',
          boxShadow: 'var(--shadow-subtle)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 'var(--shadow-medium)'
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h6" 
              component="h3" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: 'var(--purple-enterprise)', 
                fontSize: 'var(--app-title-size)',
                fontWeight: 700,
                fontFamily: 'var(--font-family)'
              }}
            >
              <ScienceIcon sx={{ mr: 1, color: 'secondary.main' }} />
              Try Sample Data
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 'var(--spacing-lg)', 
                color: 'var(--medium-gray)',
                fontFamily: 'var(--font-family)',
                fontSize: 'var(--body-text-size)'
              }}
            >
              Explore with realistic AWS billing examples
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.entries(sampleDataDetails).map(([key, sample]: [string, any]) => (
                <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => loadSampleBill(key)}
                    sx={{ 
                      flex: 1,
                      justifyContent: 'flex-start', 
                      py: 0.75, 
                      px: 1.5,
                      borderColor: key === 'super_enterprise' ? 'warning.main' : 'secondary.300',
                      bgcolor: key === 'super_enterprise' ? 'warning.50' : 'transparent',
                      '&:hover': { 
                        borderColor: key === 'super_enterprise' ? 'warning.main' : 'secondary.main', 
                        bgcolor: key === 'super_enterprise' ? 'warning.100' : 'secondary.50' 
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {sample.name} - {sample.cost}
                    </Typography>
                  </Button>
                  <Button 
                    variant="text" 
                    size="small"
                    onClick={() => {
                      setSelectedSample(key);
                      setShowSamplePreview(true);
                    }}
                    sx={{ 
                      color: 'secondary.main'
                    }}
                  >
                    Preview
                  </Button>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* AWS Bill Download Guide - Dedicated Section */}
      <Card sx={{ 
        mb: 'var(--spacing-xxl)', 
        background: 'var(--warning-bg)', 
        border: '2px solid var(--warning-blue)', 
        borderRadius: 'var(--border-radius-large)',
        boxShadow: 'var(--shadow-subtle)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 'var(--shadow-medium)'
        }
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              color: 'var(--warning-blue)', 
              fontSize: 'var(--login-title-size)',
              fontWeight: 700,
              fontFamily: 'var(--font-family)',
              mb: 'var(--spacing-xxl)' 
            }}
          >
            <DescriptionIcon sx={{ mr: 1.5, color: 'info.main', fontSize: 28 }} />
            How to Get Your AWS Bill
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 3, color: 'grey.700' }}>
                Follow these simple steps to download your AWS billing data:
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    minWidth: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    bgcolor: 'info.main', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem'
                  }}>
                    1
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800', mb: 0.5 }}>
                      Access AWS Console
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'grey.700' }}>
                      Sign in to AWS Management Console and navigate to <strong>Billing & Cost Management</strong>
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    minWidth: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    bgcolor: 'info.main', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem'
                  }}>
                    2
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800', mb: 0.5 }}>
                      Navigate to Bills
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'grey.700' }}>
                      Click <strong>"Bills"</strong> in the left navigation menu and select your desired billing period
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    minWidth: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    bgcolor: 'info.main', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem'
                  }}>
                    3
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800', mb: 0.5 }}>
                      Download Your Data
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'grey.700', mb: 1 }}>
                      <strong>Recommended:</strong> Download PDF bill (default format)<br/>
                      Alternative: Click <strong>"Download CSV"</strong> or use Cost and Usage Reports
                    </Typography>
                    <Chip 
                      label="PDF Bills Supported!" 
                      color="success" 
                      size="small" 
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                size="large"
                href="https://console.aws.amazon.com/costmanagement/home#/bills"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  textTransform: 'none',
                  background: 'var(--primary-gradient)',
                  py: 'var(--spacing-xl)',
                  px: 'var(--spacing-xxl)',
                  fontSize: 'var(--body-text-size)',
                  fontWeight: 600,
                  borderRadius: 'var(--border-radius-small)',
                  fontFamily: 'var(--font-family)',
                  boxShadow: 'var(--shadow-medium)',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    background: 'var(--primary-gradient)',
                    transform: 'translateY(-1px)',
                    boxShadow: 'var(--shadow-large)'
                  }
                }}
              >
                Open AWS Console →
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'grey.600' }}>
                Opens in new tab
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Sample Data Preview Modal */}
      <Dialog 
        open={showSamplePreview} 
        onClose={() => setShowSamplePreview(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        {selectedSample && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h5" sx={{ color: 'grey.800', fontWeight: 600, mb: 0.5 }}>
                    {sampleDataDetails[selectedSample as keyof typeof sampleDataDetails].name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.600' }}>
                    {sampleDataDetails[selectedSample as keyof typeof sampleDataDetails].description}
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setShowSamplePreview(false)}
                  sx={{ color: 'grey.500' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600, mb: 2 }}>
                  Monthly Cost: {sampleDataDetails[selectedSample as keyof typeof sampleDataDetails].cost}
                </Typography>

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                  AWS Services Included:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {sampleDataDetails[selectedSample as keyof typeof sampleDataDetails].services.map((service: string, index: number) => (
                    <Chip key={index} label={service} color="primary" variant="outlined" />
                  ))}
                </Box>

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                  Sample CSV Data Preview:
                </Typography>
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  border: '1px solid', 
                  borderColor: 'grey.300',
                  maxHeight: 300,
                  overflow: 'auto'
                }}>
                  <Typography 
                    component="pre" 
                    sx={{ 
                      fontSize: '0.875rem', 
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: 'grey.800',
                      lineHeight: 1.5,
                      m: 0
                    }}
                  >
                    {sampleDataDetails[selectedSample as keyof typeof sampleDataDetails].csvData}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
              <Button 
                variant="outlined" 
                onClick={() => {
                  const blob = new Blob([sampleDataDetails[selectedSample as keyof typeof sampleDataDetails].csvData], 
                    { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `sample-aws-bill-${selectedSample}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                sx={{ textTransform: 'none' }}
              >
                Download CSV
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  loadSampleBill(selectedSample);
                  setShowSamplePreview(false);
                }}
                sx={{ textTransform: 'none' }}
              >
                Analyze This Sample
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default BillUpload;